import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, Line } from 'recharts';

interface BillingData {
  annual_summary: any[];
  client_summary: any[];
  participation_summary: any[];
}

interface BillingViewProps {
  data: BillingData;
  title: string;
}

const formatYAxis = (tickItem: number) => {
  if (tickItem >= 1000000000) {
    return `${(tickItem / 1000000000).toFixed(1)}B`;
  }
  if (tickItem >= 1000000) {
    return `${(tickItem / 1000000).toFixed(1)}M`;
  }
  if (tickItem >= 1000) {
    return `${(tickItem / 1000).toFixed(1)}K`;
  }
  return tickItem.toString();
};

const BillingView: React.FC<BillingViewProps> = ({ data, title }) => {
  const theme = useTheme();
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A434F3', '#F33434', '#34F3A4', '#F3A434'];

  if (!data || !data.annual_summary || data.annual_summary.length === 0) {
    return (
      <Paper sx={{ p: 2, textAlign: 'center' }}>
        <Typography>No hay datos disponibles para {title}.</Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" gutterBottom>Dashboard de {title}</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ width: { xs: '100%', md: 'calc(50% - 8px)' } }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6">Facturación Anual</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.annual_summary}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="Anio" />
                <YAxis tickFormatter={formatYAxis} />
                <Tooltip formatter={(value: number) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }).format(value)} />
                <Legend />
                <Bar dataKey="Total" fill={theme.palette.primary.main} name="Total Facturado" />
                <Line type="monotone" dataKey="Total" stroke={theme.palette.secondary.main} name="Tendencia" strokeDasharray="5 5" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Box>
        <Box sx={{ width: { xs: '100%', md: 'calc(50% - 8px)' } }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6">Participación por Cliente</Typography>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={data.client_summary}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill={theme.palette.secondary.main}
                  dataKey="Total"
                  nameKey="Nombre_Cliente"
                >
                  {data.client_summary.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }).format(value)} />
              </PieChart>
            </ResponsiveContainer>
            <Box sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              maxHeight: 100,
              overflowY: 'auto',
              mt: 2,
              border: '1px solid #ccc',
              borderRadius: '4px',
              p: 1
            }}>
              {data.client_summary.map((entry, index) => (
                <Box key={`item-${index}`} sx={{ display: 'flex', alignItems: 'center', mr: 2, mb: 1, flexBasis: '45%' }}>
                  <Box sx={{ width: 14, height: 14, backgroundColor: COLORS[index % COLORS.length], mr: 1, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ flexGrow: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{entry.Nombre_Cliente}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Box>
        <Box sx={{ width: '100%' }}>
          <Paper sx={{ p: 2, mt: 2 }}>
            <Typography variant="h6">Análisis Anual</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Año</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Variación Porcentual</TableCell>
                    <TableCell align="right">Variación Moneda</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.annual_summary.map((row) => (
                    <TableRow key={row.Anio}>
                      <TableCell>{row.Anio}</TableCell>
                      <TableCell align="right">{new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }).format(row.Total)}</TableCell>
                      <TableCell align="right">{typeof row.Variacion_Porcentual === 'number' ? row.Variacion_Porcentual.toFixed(2) : 'N/A'}%</TableCell>
                      <TableCell align="right">{new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }).format(row.Variacion_Moneda)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
        <Box sx={{ width: '100%' }}>
          <Paper sx={{ p: 2, mt: 2 }}>
            <Typography variant="h6">Detalle por Cliente</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nombre Cliente</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.client_summary.map((row) => (
                    <TableRow key={row.Nombre_Cliente}>
                      <TableCell>{row.Nombre_Cliente}</TableCell>
                      <TableCell align="right">{new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }).format(row.Total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
        <Box sx={{ width: '100%' }}>
          <Paper sx={{ p: 2, mt: 2 }}>
            <Typography variant="h6">Participación Porcentual por Año y Cliente</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Año</TableCell>
                    <TableCell>Nombre Cliente</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Participación Porcentual</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.participation_summary.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>{row.Anio}</TableCell>
                      <TableCell>{row.Nombre_Cliente}</TableCell>
                      <TableCell align="right">{new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }).format(row.Total)}</TableCell>
                      <TableCell align="right">{typeof row.Participacion_porcentual === 'number' ? row.Participacion_porcentual.toFixed(2) : 'N/A'}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default BillingView;